import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { GenericForm, GenericFormField, GenericSelect } from '@/styles/GenericForm';
import { GenericAttachmentsSection, GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { StandardButton, SecondaryButton } from '@/styles/StandardButtons';
import { Invoice, Project } from '../types';
import { useUser } from '@/contexts/UserContext';
import { fetchEntityList } from '@/lib/entity-api';
import { fetchEntityAttachments } from '@/lib/entity-attachments';
import { colors } from '@/design-system/colors';
import { X } from 'lucide-react';
import { uploadInvoicePaymentAttachments, deleteInvoicePaymentAttachment } from '../services/invoicePaymentAttachments';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { useEntityComments } from '@/hooks/useEntityComments';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { Textarea } from '@/components/ui/textarea';
import { dollars, fromCents, paymentFigures, toCents, type PaymentRow } from '../lib/invoice-payment-math';


const formatInvoiceIdForDisplay = (invoice?: Invoice): string => {
  if (!invoice) return '-';
  return invoice.invoice_id;
};

interface InvoicePaymentFormProps {
  payment: any;
  projects: Project[];
  invoices: Invoice[];
  /** A NEW payment starts with these (e.g. "Create Payment" on an invoice: its project, client and what is left to pay); ignored when editing. */
  defaults?: { invoice_id?: string; project_id?: string; client_name?: string; amount?: number };
  onClose: () => void;
  onSave: (data: { paymentData: any; newAttachments: Array<{ file: File; name: string; size: number; type: string }>; removedAttachmentIds: string[] }) => Promise<any>;
  isOpen: boolean;
}

export function InvoicePaymentForm({
  payment,
  projects,
  invoices,
  defaults,
  onClose,
  onSave,
  isOpen,
}: InvoicePaymentFormProps) {
  const { companyId, user } = useUser();
  const [formData, setFormData] = useState<Record<string, any>>({});
  // Comments belong to THIS payment (entity_type 'invoice_payment' + its own id), once it exists
  const { comments, addComment, deleteComment } = useEntityComments('invoice_payment', payment?.id || null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [paidInFull, setPaidInFull] = useState(false);
  // The other payments of the selected invoice as the server lists them; Prior Payments is worked out from them (voided ones do not count)
  const [otherPayments, setOtherPayments] = useState<PaymentRow[]>([]);
  // One id per opening: a payment sent twice (double click, a retry) is saved once - the server answers the second with the first (client_request_id)
  const requestKey = useRef<string>('');
  const submitLock = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previousIsOpenRef = useRef(false);
  const initialValuesRef = useRef<Record<string, any>>({});
  const initialPaidInFullRef = useRef<boolean>(false);
  const initialPriorPaymentsRef = useRef<number>(0);
  const initialAmountRef = useRef<number>(0);
  const hasCapturedInitialValuesRef = useRef<boolean>(false);

  const uploadInvoicePaymentAttachmentsFn = useCallback(async (entityId: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user) return [];
    return uploadInvoicePaymentAttachments(entityId, files, companyId, user.id);
  }, [companyId, user]);

  // Preserves the original's silent-catch behavior (no user-facing error on
  // delete failure) by never rethrowing -- the hook's onError only fires
  // when deleteFn itself throws, which this wrapper deliberately avoids.
  const deleteInvoicePaymentAttachmentFn = useCallback(async (attachmentId: string): Promise<boolean> => {
    if (!payment?.id || !user || !companyId) return false;
    return deleteInvoicePaymentAttachment(attachmentId, user.id);
  }, [payment?.id, user, companyId]);

  const {
    pendingFiles,
    uploadedFiles: uploadedAttachments,
    setUploadedFiles: setUploadedAttachments,
    handleUpload: handleFileUploadBase,
    handleRemovePending: handleRemovePendingFile,
    handleDelete: handleDeleteAttachment,
    resetPending: resetPendingFiles,
  } = useAttachmentStaging<GenericAttachment>({
    entityId: payment?.id,
    uploadFn: uploadInvoicePaymentAttachmentsFn,
    deleteFn: deleteInvoicePaymentAttachmentFn,
    onError: setError,
  });

  const handleFileUpload = useCallback(async (files: File[]) => {
    if (!files.length || !user || !companyId) return;
    await handleFileUploadBase(files);
  }, [handleFileUploadBase, user, companyId]);

  // Normalize function for consistent comparison
  const normalize = (obj: Record<string, any>): Record<string, string> => {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, v == null ? '' : String(v).trim()])
    );
  };

  // Initialize form data
  const initialValues = useMemo(() => {
    if (payment?.id) {
      return {
        payment_number: payment.payment_number || '',
        project_id: payment.project_id || '',
        client_name: payment.client_name || '',
        invoice_id: payment.invoice_id || 'none',
        amount: payment.amount || 0,
        payment_date: payment.payment_date || new Date().toISOString().split('T')[0],
        status: payment.status || 'pending',
        notes: payment.notes || '',
      };
    }
    return {
      payment_number: '',
      project_id: defaults?.project_id || '',
      client_name: defaults?.client_name || '',
      invoice_id: defaults?.invoice_id || 'none',
      amount: defaults?.amount || 0,
      payment_date: new Date().toISOString().split('T')[0],
      status: 'pending',
      notes: '',
    };
  }, [payment, defaults]);

  // Capture initial values when form opens (like PaymentForm - capture after all state updates)
  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      // Reset capture flag when form opens
      hasCapturedInitialValuesRef.current = false;
      
      // Capture initial form data first, before setting state
      const currentInitialValues = payment?.id ? {
        payment_number: payment.payment_number || '',
        project_id: payment.project_id || '',
        client_name: payment.client_name || '',
        invoice_id: payment.invoice_id || 'none',
        amount: payment.amount || 0,
        payment_date: payment.payment_date || new Date().toISOString().split('T')[0],
        status: payment.status || 'pending',
        notes: payment.notes || '',
      } : {
        payment_number: '',
        project_id: defaults?.project_id || '',
        client_name: defaults?.client_name || '',
        invoice_id: defaults?.invoice_id || 'none',
        amount: defaults?.amount || 0,
        payment_date: new Date().toISOString().split('T')[0],
        status: 'pending',
        notes: '',
      };

      setFormData(currentInitialValues);
      setPaidInFull(false);
      setOtherPayments([]);
      requestKey.current = crypto.randomUUID();
      submitLock.current = false;
      // Clear any files staged from a previous, cancelled open -- otherwise
      // they'd silently reappear (and eventually get uploaded) the next
      // time this modal opens, even though the user never re-selected them.
      resetPendingFiles();

      // Load invoice if invoice_id is set
      if (initialValues.invoice_id && initialValues.invoice_id !== 'none') {
        const invoice = invoices.find(i => i.id === initialValues.invoice_id);
        if (invoice) {
          setSelectedInvoice(invoice);
          setFormData(prev => ({ ...prev, project_id: invoice.project_id }));
        }
      }

      // Load attachments
      if (payment?.id) {
        const loadAttachments = async () => {
          try {
            setUploadedAttachments(await fetchEntityAttachments('invoice_payment', payment.id, { ascending: false }));
          } catch (error) {
            console.error('Error loading attachments:', error);
          }
        };

        loadAttachments();
      } else {
        setUploadedAttachments([]);
      }
    }
    
    // Reset capture flag when form closes (like PaymentForm)
    if (!isOpen && previousIsOpenRef.current) {
      hasCapturedInitialValuesRef.current = false;
    }
    
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Update selected invoice and project when invoice_id changes
  useEffect(() => {
    if (formData.invoice_id && formData.invoice_id !== 'none') {
      const invoice = invoices.find(i => i.id === formData.invoice_id);
      if (invoice) {
        setSelectedInvoice(invoice);
        const updated = { ...formData, project_id: invoice.project_id };
        setFormData(updated);
        // Infer client name from project if not set
        if (!formData.client_name) {
          const proj = projects.find(p => p.id === invoice.project_id);
          const inferredClient = (proj as any)?.client_name || (proj as any)?.customer_name || (proj as any)?.owner_name || proj?.name || '';
          if (inferredClient) {
            setFormData(prev => ({ ...prev, client_name: inferredClient }));
          }
        }
      }
    } else {
      setSelectedInvoice(null);
    }
  }, [formData.invoice_id, invoices, projects]);

  // The selected invoice's other payments, from the server (the same list the Payments table reads)
  useEffect(() => {
    let cancelled = false;
    async function loadPriorPayments() {
      try {
        if (!companyId || !selectedInvoice?.id) {
          setOtherPayments([]);
          return;
        }
        const data = await fetchEntityList<any>('invoice_payment', { filters: { invoice_id: selectedInvoice.id } });
        if (!cancelled) setOtherPayments(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error loading prior payments:', err);
        if (!cancelled) setOtherPayments([]);
      }
    }
    loadPriorPayments();
    return () => { cancelled = true; };
  }, [companyId, selectedInvoice?.id, payment?.id, isOpen]);

  // Every figure of the balance summary, in whole cents (lib/invoice-payment-math.ts): a voided payment is not money paid, this payment is never counted twice
  const figures = paymentFigures({ totalAmount: selectedInvoice?.total_amount as number, payments: otherPayments, editingId: payment?.id, amount: formData.amount, status: formData.status });
  const priorPayments = dollars(figures.priorCents);

  // Capture initial values after all state updates complete (like PaymentForm - capture after async completes)
  useEffect(() => {
    if (isOpen && !hasCapturedInitialValuesRef.current) {
      // Wait for all async operations and state updates to complete
      // This includes: priorPayments loading, formData updates from invoice selection, etc.
      const timer = setTimeout(() => {
        // Capture normalized formData (after all updates)
        const normalizedFormData = normalize(formData);
        initialValuesRef.current = normalizedFormData;
        
        // Capture other state values
        initialPriorPaymentsRef.current = priorPayments;
        initialPaidInFullRef.current = paidInFull;
        initialAmountRef.current = Number(formData.amount) || 0;
        
        hasCapturedInitialValuesRef.current = true;
      }, 200); // Delay to ensure all state updates (including async priorPayments) complete
      return () => clearTimeout(timer);
    }
  }, [isOpen, priorPayments, paidInFull, formData, selectedInvoice]);

  // If Paid in full is toggled, set amount to remaining balance
  useEffect(() => {
    if (paidInFull && selectedInvoice) {
      setFormData(prev => ({ ...prev, amount: dollars(figures.leftToPayCents) }));
    }
  }, [paidInFull, selectedInvoice, figures.leftToPayCents]);

  const handleFieldChange = (name: string, value: any, updatedData?: Record<string, any>) => {
    const updated = updatedData || { ...formData, [name]: value };
    setFormData(updated);
  };

  const handleSubmit = async (data: Record<string, any>) => {
    if (isSubmitting || submitLock.current) return; // (the ref answers a second click before the state has re-rendered)
    submitLock.current = true;

    try {
      setIsSubmitting(true);
      setError(null);

      // Validate required fields
      if (!data.project_id) {
        setError('Please select a project');
        setIsSubmitting(false);
        submitLock.current = false;
        return;
      }

      const paymentData = {
        project_id: data.project_id,
        payment_number: data.payment_number?.trim() || null,
        client_name: data.client_name,
        payment_date: data.payment_date,
        status: data.status,
        invoice_id: data.invoice_id && data.invoice_id !== 'none' ? data.invoice_id : null,
        amount: data.amount,
        notes: data.notes,
        // a NEW payment carries the id of this opening, so the same request twice is saved once
        ...(payment?.id ? {} : { client_request_id: requestKey.current }),
      };

      await onSave({
        paymentData,
        newAttachments: pendingFiles.map(file => ({
          file,
          name: file.name,
          size: file.size,
          type: file.type
        })),
        removedAttachmentIds: [] // Will be handled by GenericAttachmentsSection
      });

      onClose();
    } catch (err: any) {
      console.error('Error saving payment:', err);
      setError(err?.message || 'Failed to save payment');
    } finally {
      setIsSubmitting(false);
      submitLock.current = false;
    }
  };

  const handleCloseAttempt = () => {
    // Only check for changes if we've captured initial values (like PaymentForm)
    if (!hasCapturedInitialValuesRef.current) {
      // Initial values not captured yet, allow close without warning
      onClose();
      return;
    }

    // Normalize current form data for comparison (like PaymentForm)
    const normalizedFormData = normalize(formData);
    const normalizedInitial = normalize(initialValuesRef.current);
    const formDataChanged = JSON.stringify(normalizedFormData) !== JSON.stringify(normalizedInitial);

    // Use strict comparison for numbers and booleans (like PaymentForm)
    const paidInFullChanged = paidInFull !== initialPaidInFullRef.current;
    const priorPaymentsChanged = Number(priorPayments) !== Number(initialPriorPaymentsRef.current);
    const amountChanged = Number(formData.amount) !== Number(initialAmountRef.current);
    const hasPendingFiles = pendingFiles.length > 0;

    const isActuallyDirty = formDataChanged || paidInFullChanged || priorPaymentsChanged || amountChanged || hasPendingFiles;

    if (isActuallyDirty) {
      if (window.confirm("Are you sure you want to lose all unsaved progress?")) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const mode = payment?.id ? 'edit' : 'create';
  const invoiceAmount = dollars(figures.invoiceCents);
  const currentAmount = dollars(figures.thisCents);
  const balanceDue = dollars(figures.balanceCents);

  // Payment fields (for reference, actual layout is in customLayout)
  const paymentFields = useMemo<GenericFormField[]>(() => [
    {
      name: 'payment_number',
      label: 'Payment Number',
      type: 'text',
      row: 1,
      colSpan: 1,
      placeholder: 'Enter payment number',
    },
    {
      name: 'project_id',
      label: 'Project',
      type: 'select',
      row: 1,
      colSpan: 1,
      options: projects.map(p => ({ label: p.name, value: p.id })),
      placeholder: 'Project',
      disabled: !!(formData.invoice_id && formData.invoice_id !== 'none'), // inferred from the invoice when one is chosen; free for a loose payment
    },
    {
      name: 'client_name',
      label: 'Client Name',
      type: 'text',
      row: 2,
      colSpan: 1,
      placeholder: 'Enter client name',
    },
    {
      name: 'invoice_id',
      label: 'Invoice',
      type: 'select',
      // Was `required: true`, but functionally this field IS optional --
      // it defaults to 'none' and the code sends null on submit when left
      // there (see the isBillPayment-equivalent handling below). Its own
      // <Label> never checked field.required anyway (customLayout renders
      // this file's labels by hand), so the flag was silently inert either
      // way -- removed instead of adding an asterisk to a field that
      // isn't really mandatory. Contrast with PaymentForm.tsx's `bill_id`,
      // the equivalent field for bill payments, which genuinely IS
      // required and now has both `required: true` and its asterisk.
      row: 3,
      colSpan: 1,
      options: [
        { label: 'Select Invoice', value: '__none__' },
        ...invoices.map(i => {
          const projectName = projects.find(p => p.id === i.project_id)?.name || 'Unknown Project';
          const invoiceIdDisplay = formatInvoiceIdForDisplay(i);
          return {
            label: `${invoiceIdDisplay} - ${i.name || 'Untitled Invoice'} - ${projectName}`,
            value: i.id,
          };
        }),
      ],
      placeholder: 'Select',
    },
    {
      name: 'amount',
      label: 'Amount',
      type: 'number',
      required: true,
      row: 3,
      colSpan: 1,
      placeholder: '0.00',
      prefix: '$',
      step: '0.01',
    },
    {
      name: 'payment_date',
      label: 'Payment Date',
      type: 'date',
      required: true,
      row: 4,
      colSpan: 1,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      row: 4,
      colSpan: 1,
      options: [
        { label: 'Pending', value: 'pending' },
        { label: 'Posted', value: 'posted' },
        { label: 'Voided', value: 'voided' },
      ],
      placeholder: 'Select',
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      row: 5,
      colSpan: 2, // Full width in a 2-column grid
      placeholder: 'Enter notes',
    },
  ], [projects, invoices]);

  // Custom layout for payment form
  const customLayout = (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-[1px]"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleCloseAttempt();
        }
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(formData);
        }}
        className="bg-white rounded-xl shadow-2xl flex flex-col m-4"
        style={{ width: '80vw', maxHeight: '80vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-6 border-b rounded-t-xl"
          style={{ 
            backgroundColor: colors.formHeaderFooter,
            borderColor: colors.tableBorder
          }}
        >
          <h2 className="font-form-title">
            {payment?.id ? 'Edit Payment' : 'Create Payment'}
          </h2>
          <button
            type="button"
            onClick={handleCloseAttempt}
            className="p-1 rounded-full transition-colors group"
            style={{
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X 
              className="h-5 w-5 transition-colors" 
              style={{ color: colors.tagStatusDraft }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = colors.header;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = colors.tagStatusDraft;
              }}
            />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-8">
          {error && (
            <div 
              className="px-4 py-3 rounded mb-6"
              style={{
                backgroundColor: colors.tagStatusRejectedBg,
                borderColor: colors.tagStatusRejected,
                borderWidth: '1px',
                borderStyle: 'solid',
                color: colors.tagStatusRejected
              }}
            >
              {error}
            </div>
          )}

          <div className="space-y-6">
            {payment?.payment_id && (
              <div className="space-y-2">
                <Label htmlFor="invpay_payment_id" className="font-form-label">Payment ID</Label>
                <Input id="invpay_payment_id" value={payment.payment_id} readOnly className="font-form-input generic-form-field bg-gray-100 cursor-not-allowed" />
              </div>
            )}
            {/* Row 1: Payment Number and Project */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="payment_number" className="font-form-label">Payment Number</Label>
                <Input
                  id="payment_number"
                  value={formData.payment_number || ''}
                  onChange={(e) => {
                    const updated = { ...formData, payment_number: e.target.value };
                    setFormData(updated);
                    handleFieldChange('payment_number', e.target.value, updated);
                  }}
                  placeholder="Enter payment number"
                  className="font-form-input generic-form-field"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project_id" className="font-form-label">Project</Label>
                <GenericSelect
                  id="project_id"
                  value={formData.project_id || ''}
                  onValueChange={(value) => {
                    const updated = { ...formData, project_id: value };
                    setFormData(updated);
                    handleFieldChange('project_id', value, updated);
                  }}
                  disabled={!!(formData.invoice_id && formData.invoice_id !== 'none')}
                  required
                  placeholder="Project"
                  className={formData.invoice_id && formData.invoice_id !== 'none' ? 'cursor-not-allowed' : undefined}
                  options={projects.map(p => ({ label: p.name, value: p.id }))}
                />
              </div>
            </div>

            {/* Row 2: Client Name (half width) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="client_name" className="font-form-label">Client Name</Label>
                <Input
                  id="client_name"
                  value={formData.client_name || ''}
                  onChange={(e) => {
                    const updated = { ...formData, client_name: e.target.value };
                    setFormData(updated);
                    handleFieldChange('client_name', e.target.value, updated);
                  }}
                  placeholder="Enter client name"
                  className="font-form-input generic-form-field"
                />
              </div>
              <div></div> {/* Empty space for half width */}
            </div>

            {/* Row 3: Invoice and Amount */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="invoice_id" className="font-form-label">Invoice</Label>
                <GenericSelect
                  id="invoice_id"
                  value={formData.invoice_id && formData.invoice_id !== 'none' ? formData.invoice_id : ''}
                  onValueChange={(value) => {
                    // Root-cause fix (2026-09-22): the manual "Select Invoice"
                    // item used to be hardcoded to value="__none__", which
                    // never matched the 'none' sentinel this file's own
                    // initial state/submit logic actually checks for
                    // (formData.invoice_id !== 'none') -- clearing this field
                    // silently saved the literal string "__none__" as
                    // invoice_id instead of the null the submit handler
                    // intended. GenericSelect's clear item always calls back
                    // with a real '', so `value || 'none'` is correct again.
                    const updated = { ...formData, invoice_id: value || 'none' };
                    setFormData(updated);
                    handleFieldChange('invoice_id', value || 'none', updated);
                  }}
                  placeholder="Select"
                  contentClassName="max-h-[18.75rem] overflow-y-auto"
                  options={
                    invoices.length > 0
                      ? invoices.map((invoice) => {
                          const projectName = projects.find(p => p.id === invoice.project_id)?.name || 'Unknown Project';
                          const invoiceIdDisplay = formatInvoiceIdForDisplay(invoice);
                          return {
                            value: invoice.id,
                            label: `${invoiceIdDisplay} - ${invoice.name || 'Untitled Invoice'} - ${projectName}`,
                          };
                        })
                      : [{ value: 'no-invoices', label: 'No invoices available', disabled: true }]
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount" className="font-form-label">Amount ($)</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount || ''}
                  onChange={(e) => {
                    const cents = toCents(e.target.value);
                    const updated = { ...formData, amount: fromCents(cents) };
                    setFormData(updated);
                    handleFieldChange('amount', fromCents(cents), updated);
                  }}
                  placeholder="$0.00"
                  className="font-form-input generic-form-field [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  onKeyDown={(e: React.KeyboardEvent) => {
                    // Prevent arrow keys from changing number values
                    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e: React.WheelEvent) => {
                    // Prevent trackpad/mouse wheel from changing number values
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                    // Prevent wheel events on focused number inputs
                    e.currentTarget.addEventListener('wheel', (event) => {
                      event.preventDefault();
                    }, { passive: false });
                  }}
                />
                <div className="mt-2 flex items-center gap-2">
                  <input
                    id="paidInFull"
                    type="checkbox"
                    checked={paidInFull}
                    onChange={(e) => setPaidInFull(e.target.checked)}
                    className="rounded"
                    style={{ borderColor: colors.tableBorder }}
                  />
                  <Label htmlFor="paidInFull" className="text-sm font-normal">Paid in full</Label>
                </div>
              </div>
            </div>

            {/* Row 4: Payment Date and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="payment_date" className="font-form-label">Payment Date</Label>
                <Input
                  id="payment_date"
                  type="date"
                  value={formData.payment_date || ''}
                  onChange={(e) => {
                    const updated = { ...formData, payment_date: e.target.value };
                    setFormData(updated);
                    handleFieldChange('payment_date', e.target.value, updated);
                  }}
                  className="font-form-input generic-form-field"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status" className="font-form-label">Status</Label>
                <GenericSelect
                  id="status"
                  value={formData.status || ''}
                  onValueChange={(value) => {
                    const updated = { ...formData, status: value };
                    setFormData(updated);
                    handleFieldChange('status', value, updated);
                  }}
                  placeholder="Select"
                  required
                  options={[
                    { label: 'Pending', value: 'pending' },
                    { label: 'Posted', value: 'posted' },
                    { label: 'Voided', value: 'voided' },
                  ]}
                />
              </div>
            </div>

            {/* Row 5: Notes (full width) */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="font-form-label">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => {
                  const updated = { ...formData, notes: e.target.value };
                  setFormData(updated);
                  handleFieldChange('notes', e.target.value, updated);
                }}
                placeholder="Enter notes"
                rows={3}
              />
            </div>

            {/* Balance Summary */}
            <div className="flex justify-end mt-2">
              <div className="w-full md:w-1/2 space-y-2">
                <div className="grid grid-cols-[1fr,4.5rem,4.5rem] items-center text-sm">
                  <span className="justify-self-end">Invoice Amount</span>
                  <span></span>
                  <span className="text-right">${invoiceAmount.toFixed(2)}</span>
                </div>
                <div className="grid grid-cols-[1fr,4.5rem,4.5rem] items-center text-sm">
                  <span className="justify-self-end">Prior Payments</span>
                  <span></span>
                  <span className="text-right">${Number(priorPayments || 0).toFixed(2)}</span>
                </div>
                <div className="grid grid-cols-[1fr,4.5rem,4.5rem] items-center text-sm">
                  <span className="justify-self-end">This Payment</span>
                  <span></span>
                  <span className="text-right">${currentAmount.toFixed(2)}</span>
                </div>
                <div 
                  className="grid grid-cols-[1fr,4.5rem,4.5rem] items-center text-sm font-semibold border-t pt-2"
                  style={{ 
                    color: colors.header,
                    borderColor: colors.tableBorder
                  }}
                >
                  <span className="justify-self-end">Balance Due</span>
                  <span></span>
                  <span className="text-right">${balanceDue.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Attachments Section */}
            <div className="mt-6">
              <GenericAttachmentsSection
                attachments={uploadedAttachments}
                pendingFiles={pendingFiles}
                onUpload={handleFileUpload}
                onDelete={handleDeleteAttachment}
                onRemovePending={handleRemovePendingFile}
                projectId={formData.project_id || ''} mode={mode}
              />
            </div>

            <div className="mt-6">
              <GenericCommentsSection
                comments={comments}
                onAddComment={async (content, audience) => { await addComment(content, audience); }}
                onDeleteComment={async (commentId) => {
                  if (!window.confirm('Are you sure you want to delete this comment?')) return;
                  await deleteComment(commentId);
                }}
                mode={mode}
                audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
                placeholder={mode === 'edit' ? 'Add a comment...' : 'Comments are available after creating the payment'}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div 
          className="border-t flex justify-end gap-4 rounded-b-xl p-6"
          style={{ 
            backgroundColor: colors.formHeaderFooter,
            borderColor: colors.tableBorder
          }}
        >
          <StandardButton
            onClick={handleCloseAttempt}
            type="button"
            customVariant="white"
          >
            Cancel
          </StandardButton>
          <SecondaryButton
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </SecondaryButton>
        </div>
      </form>
    </div>
  );

  if (!isOpen) return null;

  // Render in a portal to ensure it's at the document body level
  // This ensures fixed positioning works correctly and covers the entire screen
  return createPortal(customLayout, document.body);
}

